import {compact} from '@benchling/js-utils';
import {css} from 'aphrodite';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {Tag} from 'folder-items/FileConstants';
import {getAccessibleOrInaccessibleId, isInaccessible} from 'graphql-client/InaccessibleInfo';
import TagValueViewer from 'metadata/TagValueViewer';
import RequestV2SubmissionChip from 'processes/requests_v2/chips/RequestV2SubmissionChip';
import TaskStatusIcon from 'processes/status/TaskStatusIcon';
import EditableStudiesList from 'studies/studyable/EditableStudiesList';
import Avatar, {HasAvatar, NO_AVATAR_URL} from 'ui-library/avatar/Avatar';
import {AvatarSize} from 'ui-library/avatar/AvatarConstants';
import {ChipSize} from 'ui-library/chips/Chip';
import {ChipPopoverInfoField} from 'ui-library/chips/ChipPopoverContent';
import {generateKeyForSchemaField} from 'ui-library/chips/ChipPopoverContentUtils';
import ProcessChip from 'ui-library/chips/ProcessChip';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import AvatarIcon from 'ui-library/icons/AvatarIcon';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {readableTime} from 'util/DateUtil';

import {CHIP_POPOVER_STYLES, GetChipPopoverContentsArgs} from './ChipUtilConstants';
import {ProcessTaskForPopoverGql} from './graphql';

export const NO_VALUE = 'None';

export function getIdField(processTask: ProcessTaskForPopoverGql): ChipPopoverInfoField {
  return {
    label: 'ID',
    value: <>{processTask.humanId}</>,
  };
}

export function getNameField(
  processTask: ProcessTaskForPopoverGql,
  icon: BenchlingIconName | null
): ChipPopoverInfoField {
  return {
    label: 'Name',
    value: (
      <>
        {icon != null ? <BenchlingIcon icon={icon} /> : null}
        &nbsp; {processTask.name}
      </>
    ),
  };
}

export function ProcessUserDisplay({user}: {user: HasAvatar}): JSX.Element {
  return (
    <span>
      <Avatar entity={user} size={AvatarSize.XXSMALL} />
      &nbsp;
      {user.name}
    </span>
  );
}

export function getAssigneeField(processTask: ProcessTaskForPopoverGql): ChipPopoverInfoField {
  return {
    label: 'Assignee',
    value:
      processTask.assignee != null ? (
        <ProcessUserDisplay user={processTask.assignee} />
      ) : (
        <span>
          <AvatarIcon avatarUrl={NO_AVATAR_URL} size={AvatarSize.XSMALL} /> &nbsp; Unassigned
        </span>
      ),
  };
}

export function getScheduledOnField(processTask: ProcessTaskForPopoverGql): ChipPopoverInfoField {
  return {
    label: 'Scheduled on',
    value: (
      <>
        {processTask.scheduledOnDate != null
          ? readableTime(processTask.scheduledOnDate, {showTime: false})
          : 'Not scheduled'}
      </>
    ),
  };
}

export function getStatusField(processTask: ProcessTaskForPopoverGql): ChipPopoverInfoField {
  return {
    label: 'Status',
    value: (
      <span className={css(CHIP_POPOVER_STYLES.statusWrapper)}>
        <TaskStatusIcon type={processTask.status.statusType} /> {processTask.status.displayName}
      </span>
    ),
  };
}

export function getRequestV2SubmissionField(
  processTask: ProcessTaskForPopoverGql
): ChipPopoverInfoField | null {
  if (!BenchlingConfig.REQUESTS_V2_ENABLE) {
    return null;
  }

  const requestV2Submission = processTask.process.requestV2Submission;
  return {
    label: 'Request',
    value:
      requestV2Submission == null ? (
        NO_VALUE
      ) : (
        <RequestV2SubmissionChip
          size={ChipSize.MINI}
          id={getAccessibleOrInaccessibleId(requestV2Submission)}
        />
      ),
  };
}

export function getStudiesField(processTask: ProcessTaskForPopoverGql): ChipPopoverInfoField | null {
  if (!BenchlingConfig.STUDIES_ENABLE) {
    return null;
  }

  return {
    label: 'Studies',
    value: (
      <EditableStudiesList
        studyable={processTask}
        isWritable={false}
        renderWhileLoading={() => <HorizontalThrobber />}
      />
    ),
  };
}

export default function getDefaultProcessTaskChipPopoverContent({
  processTask,
  icon,
}: GetChipPopoverContentsArgs): Array<ChipPopoverInfoField> {
  return compact([
    getNameField(processTask, icon),
    {
      label: 'Schema',
      value: <>{isInaccessible(processTask.taskSchema) ? 'No access' : processTask.taskSchema.name}</>,
    },
    getAssigneeField(processTask),
    getScheduledOnField(processTask),
    getStatusField(processTask),
    {
      label: 'Task group',
      value: <ProcessChip id={processTask.process.id} size={ChipSize.MINI} />,
    },
    getRequestV2SubmissionField(processTask),
    getStudiesField(processTask),
    ...(processTask.fields as Array<Tag>).map((tag) => ({
      label: tag.name,
      generateStableKey: () => generateKeyForSchemaField(tag),
      value: (
        <TagValueViewer
          tag={tag}
          chipSize={ChipSize.MINI}
          shouldPreviewImages={true}
          showChipsAsLinks={true}
          summarize={true}
          summarizeThreshold={3}
        />
      ),
    })),
  ]);
}
